/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { MessageStatus, MessageTransport } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface TrackedMessageProps {
  id: string;
  recipientName: string | null;
  recipientAddress: string;
  subject: string | null;
  body: string;
  fromName: string | null;
  fromAddress: string | null;
  doNotDivert: boolean | null;
  medium: MessageTransport | null;
  status: MessageStatus;
  vendorId: string | null;
  vendorMessageId: string | null;
  referenceNumber: string | null;
  vendorConversationId: string | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class TrackedMessageRecord extends Record<TrackedMessageProps>({
  id: null as any,
  recipientName: null as any,
  recipientAddress: null as any,
  subject: null as any,
  body: null as any,
  fromName: null as any,
  fromAddress: null as any,
  doNotDivert: null as any,
  medium: null as any,
  status: null as any,
  vendorId: null as any,
  vendorMessageId: null as any,
  referenceNumber: null as any,
  vendorConversationId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    recipientName: string | null;
    recipientAddress: string;
    subject: string | null;
    body: string;
    fromName: string | null;
    fromAddress: string | null;
    doNotDivert: boolean | null;
    medium: MessageTransport | null;
    status: MessageStatus;
    vendorId: string | null;
    vendorMessageId: string | null;
    referenceNumber: string | null;
    vendorConversationId: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): TrackedMessageRecord {
    if (json == null) {
      throw new Error('TrackedMessageRecord.fromJS: parameter cannot be null');
    }
    return new TrackedMessageRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'TrackedMessageJson.id',
      ),
      recipientName: json.recipientName,
      recipientAddress: getFromOrThrow(
        json,
        (j) => j.recipientAddress,
        'TrackedMessageJson.recipientAddress',
      ),
      subject: json.subject,
      body: getFromOrThrow(json, (j) => j.body, 'TrackedMessageJson.body'),
      fromName: json.fromName,
      fromAddress: json.fromAddress,
      doNotDivert: json.doNotDivert,
      medium: json.medium,
      status: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'status', MessageStatus.Created),
        'TrackedMessageJson.status',
      ),
      vendorId: json.vendorId,
      vendorMessageId: json.vendorMessageId,
      referenceNumber: json.referenceNumber,
      vendorConversationId: json.vendorConversationId,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    recipientName: string | null;
    recipientAddress: string;
    subject: string | null;
    body: string;
    fromName: string | null;
    fromAddress: string | null;
    doNotDivert: boolean | null;
    medium: MessageTransport | null;
    status: MessageStatus;
    vendorId: string | null;
    vendorMessageId: string | null;
    referenceNumber: string | null;
    vendorConversationId: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [TrackedMessageRecord, Error[]] {
    if (json == null) {
      throw new Error('TrackedMessageRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new TrackedMessageRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'TrackedMessageJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      recipientName: tryOrDefault(
        () => json.recipientName,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      recipientAddress: tryOrDefault(
        () =>
          getFromOrThrow(json, (j) => j.recipientAddress, 'TrackedMessageJson.recipientAddress'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      subject: tryOrDefault(
        () => json.subject,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      body: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.body, 'TrackedMessageJson.body'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      fromName: tryOrDefault(
        () => json.fromName,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      fromAddress: tryOrDefault(
        () => json.fromAddress,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      doNotDivert: tryOrDefault(
        () => json.doNotDivert,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      medium: tryOrDefault(
        () => json.medium,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      status: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'status', MessageStatus.Created),
            'TrackedMessageJson.status',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vendorId: tryOrDefault(
        () => json.vendorId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vendorMessageId: tryOrDefault(
        () => json.vendorMessageId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      referenceNumber: tryOrDefault(
        () => json.referenceNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vendorConversationId: tryOrDefault(
        () => json.vendorConversationId,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: TrackedMessageProps) {
    super(props);
    if (props == null) {
      throw new Error('TrackedMessageRecord constructor parameter cannot be null');
    }
  }
}

export abstract class TrackedMessageViewModelBase implements TrackedMessageProps {
  protected readonly _props: TrackedMessageProps;
  private _json?: TrackedMessageProps;

  get id(): string {
    return this._props.id;
  }
  get recipientName(): string | null {
    return this._props.recipientName;
  }
  get recipientAddress(): string {
    return this._props.recipientAddress;
  }
  get subject(): string | null {
    return this._props.subject;
  }
  get body(): string {
    return this._props.body;
  }
  get fromName(): string | null {
    return this._props.fromName;
  }
  get fromAddress(): string | null {
    return this._props.fromAddress;
  }
  get doNotDivert(): boolean | null {
    return this._props.doNotDivert;
  }
  get medium(): MessageTransport | null {
    return this._props.medium;
  }
  get status(): MessageStatus {
    return this._props.status;
  }
  get vendorId(): string | null {
    return this._props.vendorId;
  }
  get vendorMessageId(): string | null {
    return this._props.vendorMessageId;
  }
  get referenceNumber(): string | null {
    return this._props.referenceNumber;
  }
  get vendorConversationId(): string | null {
    return this._props.vendorConversationId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isEmail() {
    return this._props.medium === MessageTransport.Email;
  }
  public get isSms() {
    return this._props.medium === MessageTransport.Sms;
  }
  public get isCreated() {
    return this._props.status === MessageStatus.Created;
  }
  public get isFailed() {
    return this._props.status === MessageStatus.Failed;
  }
  public get isReceivedByRecipient() {
    return this._props.status === MessageStatus.ReceivedByRecipient;
  }
  public get isSentToProvider() {
    return this._props.status === MessageStatus.SentToProvider;
  }
  public get isSentToRecipient() {
    return this._props.status === MessageStatus.SentToRecipient;
  }

  public isAnyMedium(...messageTransport: MessageTransport[]) {
    return this._props.medium != null && messageTransport.includes(this._props.medium);
  }
  public isAnyStatus(...messageStatus: MessageStatus[]) {
    return messageStatus.includes(this._props.status);
  }

  constructor(props: TrackedMessageProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        recipientName: this._props.recipientName,
        recipientAddress: this._props.recipientAddress,
        subject: this._props.subject,
        body: this._props.body,
        fromName: this._props.fromName,
        fromAddress: this._props.fromAddress,
        doNotDivert: this._props.doNotDivert,
        medium: this._props.medium,
        status: this._props.status,
        vendorId: this._props.vendorId,
        vendorMessageId: this._props.vendorMessageId,
        referenceNumber: this._props.referenceNumber,
        vendorConversationId: this._props.vendorConversationId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllTrackedMessageEntities = (state: AppState) =>
  state.entityStores.trackedMessage.entities;

export const selectTrackedMessageById = (id: string) => (state: AppState) =>
  state.entityStores.trackedMessage.entities.get(id);

export const selectTrackedMessageMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.trackedMessage.meta.get(id);

export const selectTrackedMessageAndMetadataById = (id: string) =>
  createSelector(
    selectTrackedMessageById(id),
    selectTrackedMessageMetadataById(id),
    (trackedMessage, meta) => ({ trackedMessage, meta }),
  );

export function selectTrackedMessageEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllTrackedMessageEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, TrackedMessageRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectTrackedMessageEntitiesByRecipientName(recipientName: string) {
  return createSelector(selectAllTrackedMessageEntities, (entities) =>
    entities.filter((e) => e.recipientName === recipientName).valueSeq(),
  );
}

export function selectTrackedMessageEntitiesByRecipientAddress(recipientAddress: string) {
  return createSelector(selectAllTrackedMessageEntities, (entities) =>
    entities.filter((e) => e.recipientAddress === recipientAddress).valueSeq(),
  );
}

export function selectTrackedMessageEntitiesBySubject(subject: string) {
  return createSelector(selectAllTrackedMessageEntities, (entities) =>
    entities.filter((e) => e.subject === subject).valueSeq(),
  );
}

export function selectTrackedMessageEntitiesByBody(body: string) {
  return createSelector(selectAllTrackedMessageEntities, (entities) =>
    entities.filter((e) => e.body === body).valueSeq(),
  );
}

export function selectTrackedMessageEntitiesByFromName(fromName: string) {
  return createSelector(selectAllTrackedMessageEntities, (entities) =>
    entities.filter((e) => e.fromName === fromName).valueSeq(),
  );
}

export function selectTrackedMessageEntitiesByFromAddress(fromAddress: string) {
  return createSelector(selectAllTrackedMessageEntities, (entities) =>
    entities.filter((e) => e.fromAddress === fromAddress).valueSeq(),
  );
}

export function selectTrackedMessageEntitiesByDoNotDivert(doNotDivert: boolean) {
  return createSelector(selectAllTrackedMessageEntities, (entities) =>
    entities.filter((e) => e.doNotDivert === doNotDivert).valueSeq(),
  );
}

export function selectTrackedMessageEntitiesByMedium(medium: MessageTransport) {
  return createSelector(selectAllTrackedMessageEntities, (entities) =>
    entities.filter((e) => e.medium === medium).valueSeq(),
  );
}

export function selectTrackedMessageEntitiesByStatus(status: MessageStatus) {
  return createSelector(selectAllTrackedMessageEntities, (entities) =>
    entities.filter((e) => e.status === status).valueSeq(),
  );
}

export function selectTrackedMessageEntitiesByVendorId(vendorId: string) {
  return createSelector(selectAllTrackedMessageEntities, (entities) =>
    entities.filter((e) => e.vendorId === vendorId).valueSeq(),
  );
}

export function selectTrackedMessageEntitiesByVendorMessageId(vendorMessageId: string) {
  return createSelector(selectAllTrackedMessageEntities, (entities) =>
    entities.filter((e) => e.vendorMessageId === vendorMessageId).valueSeq(),
  );
}

export function selectTrackedMessageEntitiesByReferenceNumber(referenceNumber: string) {
  return createSelector(selectAllTrackedMessageEntities, (entities) =>
    entities.filter((e) => e.referenceNumber === referenceNumber).valueSeq(),
  );
}

export function selectTrackedMessageEntitiesByVendorConversationId(vendorConversationId: string) {
  return createSelector(selectAllTrackedMessageEntities, (entities) =>
    entities.filter((e) => e.vendorConversationId === vendorConversationId).valueSeq(),
  );
}
//#endregion Selectors
